import React, { useState, useCallback } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Row,
  Col,
  Typography,
  Empty,
  Tooltip,
} from 'antd';
import { CreditCardOutlined } from '@ant-design/icons';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { getPendingCardTransactions } from '../../services/cardService';
import RegisterCardModal from '../../components/cards/RegisterCardModal';
import { formatDate } from '../../utils/format';
import useGetCards from '../../hooks/cards/useGetCards';

const { Title } = Typography;

const PendingRegistrations = () => {
  const [selectedCard, setSelectedCard] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const {
    data: cards = [],
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
    error,
    refetch,
  } = useGetCards({ status: 'requested,pending-funding', card_type: 'physical' });

  const handleRegisterCard = (card) => {
    setSelectedCard(card);
    setModalVisible(true);
  };

  const handleRowClick = (record) => {
    setSelectedCard(record);
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Card Holder',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Currency',
      dataIndex: 'dest_currency_code',
      key: 'dest_currency_code',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 150,
      render: (_, record) => (
        <span>
          {record.dest_amount.toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color="warning">PENDING</Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      width: 120,
      render: (timestamp) => formatDate(timestamp),
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Tooltip title="Register Card">
          <Button
            type="primary"
            icon={<CreditCardOutlined />}
            onClick={() => handleRegisterCard(record)}
          >
            Register
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="layout-content">
      <Card bordered={false}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={4} style={{ margin: 0 }}>Pending Card Registrations</Title>
          </Col>
        </Row>

        <Table
          dataSource={cards}
          columns={columns}
          rowKey="id"
          loading={isPending && cards.length === 0}
          pagination={false}
          scroll={{ x: 1020 }}
          locale={{
            emptyText: error ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={error}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No pending registrations"
              />
            ),
          }}
          onRow={(record) => ({
            onClick: (e) => {
              if (
                e.target.closest('button') &&
                e.target.closest('button').textContent?.toLowerCase().includes('register')
              ) {
                return;
              }
              handleRowClick(record);
            },
            style: { cursor: 'pointer' },
          })}
        />

        <RegisterCardModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          card={selectedCard}
          onSuccess={() => {
            refetch();
            setModalVisible(false);
          }}
        />
      </Card>
    </div>
  );
};

export default PendingRegistrations;
